// Mulch recipe — plan §4.2: scatter(roundedBox, aspect≈4, rotJitter=1) ×3 layers at offset phases,
// over a dark fbm. Immutable built-in root (never mutate — extend/fork instead).
//
// The plan flags this as needing "multi-pass of the SAME primitive with per-layer params" — which
// the layer model already supports directly (three `scatter` layers with distinct ids). The three
// layers are decorrelated by (a) different `density` values (so their lattices never align) and
// (b) different `colorJitterSeed`s (so the same cell index yields different colours per layer).
// Using the same density with only a seed change would stack chips exactly on top of each other.
//
// SCALE CONTRACT:
//   bed    ~1.4 m dark fbm — colour only (the soil/shadow visible between chips)
//   chips  ~7/9/11 cm lattices, ~8/6/5 mm relief, stacked coarse→fine via heightBlend so the
//          higher chip wins per-pixel: this is what reads as OVERLAPPING debris rather than a
//          cross-fade (plan §4.4: heightBlend "is not optional")
//   grain  ~1.5 cm, ~0.3 mm — fibrous surface texture on the chips
//
// HONEST EXPECTATION (plan §4.3): "good, slightly synthetic" — real mulch has fibrous, frayed chip
// EDGES which a rounded-box SDF cannot produce; the 3-layer stack mitigates by making the silhouette
// of the pile complex even though each chip is smooth.
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";

export const MULCH_RECIPE_ID = "mulch";

/** Shared shape params for the three chip layers (the "same primitive, per-layer params" pattern).
 *  Set3 §4.4 — rotJitter is full (chips lie every which way) but PLACEMENT jitter is the fixed
 *  0.7 default: with the old single rotJitter arg scaling BOTH, a full-rotation layer also got
 *  full-swing placement, which snapped the chip lattice back into visible rows. */
const CHIP_SHAPE = {
	shape: 1,        // rounded box — the chip/shred silhouette
	aspect: 4,       // ≈4:1 elongated, per the plan
	rotJitter: 1,    // full rotation
	placeJitter: 0.7, // fixed placement jitter (Set3 §4.4)
	// Set3 §4.1 — presence ~0.55 per layer: the chip lattice disappears into an organic scatter,
	// and the 3-lattice Moiré (the zoomed-out vertical brown lines) vanishes with it.
	presence: 0.55,
} as const;

export const mulchRecipe = new MaterialRecipe({
	id: MULCH_RECIPE_ID,
	name: "Mulch",
	roughness: 0.92,
	specStrength: 0, // dry organic matter — no coherent highlight (see Recipe_Dirt)

	params: [
		new ParamSchema({key: "bed_scale", label: "Bed scale (m)", kind: "scalar", min: 0.1, max: 10, default: 1.4}),
		// Very dark: the bed is what shows between chips, and mulch beds read almost black in shadow.
		new ParamSchema({key: "bed_ramp", label: "Bed ramp", kind: "ramp", default: [0, 0.08, 0.06, 0.05, 0.5, 0.14, 0.11, 0.08, 1, 0.2, 0.16, 0.12]}),
		new ParamSchema({key: "chip1_density", label: "Chip layer 1 spacing (m)", kind: "scalar", min: 0.01, max: 1, default: 0.07}),
		new ParamSchema({key: "chip2_density", label: "Chip layer 2 spacing (m)", kind: "scalar", min: 0.01, max: 1, default: 0.09}),
		new ParamSchema({key: "chip3_density", label: "Chip layer 3 spacing (m)", kind: "scalar", min: 0.01, max: 1, default: 0.11}),
		// Warm browns spanning bark→weathered-tan. Chips are colour-jittered per cell, so the ramp's
		// full span is what produces the characteristic speckled brown mass.
		new ParamSchema({key: "chip_ramp", label: "Chip ramp", kind: "ramp", default: [0, 0.24, 0.15, 0.09, 0.35, 0.4, 0.26, 0.15, 0.7, 0.55, 0.4, 0.24, 1, 0.68, 0.55, 0.38]}),
		// Set3 §3.6.3 — per-layer colour ranges: layers 2-3 bake their OWN darker ramps (literals)
		// instead of sharing chip_ramp.  Distinct ranges stop the 3-lattice Moiré doubling as a
		// 3-tone crosshatch, and keep the zoomed-out mean converging to a uniform dark brown.
		//   chips1  chip_ramp (the exposed top layer — the full bark→tan span)
		//   chips2  chip_ramp2 (shifted darker — the partially-buried chips)
		//   chips3  chip_ramp3 (darkest — the deep/rotten chips at the bed)
		new ParamSchema({key: "chip2_ramp", label: "Chip layer 2 ramp", kind: "ramp", default: [0, 0.2, 0.12, 0.07, 0.35, 0.34, 0.21, 0.12, 0.7, 0.48, 0.33, 0.19, 1, 0.58, 0.44, 0.28]}),
		new ParamSchema({key: "chip3_ramp", label: "Chip layer 3 ramp", kind: "ramp", default: [0, 0.16, 0.1, 0.06, 0.35, 0.28, 0.17, 0.1, 0.7, 0.4, 0.27, 0.16, 1, 0.5, 0.36, 0.23]}),
		new ParamSchema({key: "chip1_height", label: "Chip layer 1 relief (m)", kind: "scalar", min: 0, max: 0.1, default: 0.008}),
		new ParamSchema({key: "chip2_height", label: "Chip layer 2 relief (m)", kind: "scalar", min: 0, max: 0.1, default: 0.006}),
		new ParamSchema({key: "chip3_height", label: "Chip layer 3 relief (m)", kind: "scalar", min: 0, max: 0.1, default: 0.005}),
		// Small relative to the chip reliefs: a chip should win or lose CRISPLY (it is a discrete
		// object), unlike dirt's pebbles which are half-buried.
		new ParamSchema({key: "chip_softness", label: "Chip blend softness", kind: "scalar", min: 0.0001, max: 0.05, default: 0.001}),
		new ParamSchema({key: "grain_scale", label: "Fibre scale (m)", kind: "scalar", min: 0.001, max: 0.2, default: 0.015}),
		new ParamSchema({key: "grain_height", label: "Fibre relief (m)", kind: "scalar", min: 0, max: 0.01, default: 0.0003}),
	],
	layers: [
		new MaterialLayer({
			id: "bed",
			primitive: "fbm",
			args: {
				scale: {ref: "bed_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.55,
				mode: 3,
				colorRamp: {ref: "bed_ramp"},
				heightScale: 0,
			},
			blend: "over",
			contributes: "color",
		}),
		// Chip layers 1–3: coarsest/tallest first. Each heightBlends against the layers below it, so
		// where two chips overlap the taller one's colour wins (plan §4.4). The per-layer
		// colorJitterSeed decorrelates the colour hash so overlapping chips differ in tone.
		new MaterialLayer({
			id: "chips1",
			primitive: "scatter",
			args: {
				...CHIP_SHAPE,
				density: {ref: "chip1_density"},
				colorRamp: {ref: "chip_ramp"},
				height: {ref: "chip1_height"},
				colorJitterSeed: 101,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "chip_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "chips2",
			primitive: "scatter",
			args: {
				...CHIP_SHAPE,
				density: {ref: "chip2_density"},
				colorRamp: {ref: "chip2_ramp"},
				height: {ref: "chip2_height"},
				colorJitterSeed: 211,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "chip_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "chips3",
			primitive: "scatter",
			args: {
				...CHIP_SHAPE,
				density: {ref: "chip3_density"},
				colorRamp: {ref: "chip3_ramp"},
				height: {ref: "chip3_height"},
				colorJitterSeed: 307,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "chip_softness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "fibre",
			primitive: "fbm",
			args: {
				scale: {ref: "grain_scale"},
				octaves: 3,
				lacunarity: 2.4,
				persistence: 0.55,
				mode: 3,
				heightScale: {ref: "grain_height"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
